/* eslint-disable react-refresh/only-export-components */
import { cn } from "@/lib/utils";
import { ToolTipInfo } from "@/Pages/Brand/DashBoard/PromoteAd/Components/Components";
import { TopPostCard } from "@/Pages/Influencer/PublicProfile/Component";
import { useAuth } from "@/Providers/Auth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { FaPlus } from "react-icons/fa";
import { PiSparkleBold } from "react-icons/pi";

export const LiveAnchorsCollabSkeleton = () => {
  return (
    <div className="animate-pulse space-y-4">
      <div className="space-y-2">
        <div className="h-[30px] w-[200px] bg-[#2b1d1f] rounded-md"></div>
        <div className="h-[20px] w-[300px] bg-[#2b1d1f] rounded-md"></div>
      </div>
      <div className="h-[200px] w-full bg-[#2b1d1f] rounded-md"></div>
    </div>
  );
};

export const CollabStatusChip = ({
  icon,
  statusText,
  textColor,
  bgColor,
  info,
}: CollabStatusChipStatus) => {
  return (
    <span
      className={cn(
        "text-xs bg-[#490000] text-[12px] text-white rounded-sm flex gap-2 py-2 px-3",
        bgColor && bgColor
      )}
    >
      {icon}
      <span
        className={cn(
          "text-[#FFA2A2] font-[Inter] text-[12px] not-italic  leading-[normal] uppercase",
          textColor && textColor
        )}
      >
        {statusText}
      </span>
      {info && <ToolTipInfo Content={info} />}
    </span>
  );
};

export const InfoToolTip = ({
  text,
  trigger,
}: {
  text: string | React.ReactNode;
  trigger?: React.ReactNode;
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {trigger ? trigger : <InfoIcon size={15} />}
        </TooltipTrigger>
        <TooltipContent className="bg-[#262626] border-none max-w-xs text-[#A6A6A6] whitespace-pre-wrap break-words">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const CollabCard = ({ text, children, postFeedUrl, metrics }) => {
  const authState = useAuth();
  return (
    <div className="bg-[#1B1F23] rounded-lg px-3 py-3 gap-4 flex flex-col w-[300px] min-h-[473px]">
      {/* <p className="text-[#C7C3C3] font-[Inter] text-xs not-italic font-normal leading-[22px]">
        <span className="line-clamp-3">{text}</span>
        <span
          className="text-[#3771C8] cursor-pointer hover:underline"
          onClick={() => OpenInNewWindow({ link: postFeedUrl })}
        >
          ...View more
        </span>
      </p> */}
      <div className="p-1 border border-[#262626] rounded-sm ">
        <TopPostCard
          profile={authState?.loggedUser?.profile}
          name={authState?.loggedUser?.name}
          postDescription={text}
          postLink={postFeedUrl}
          tagline={authState?.loggedUser?.tagline}
          likes={metrics?.likesCount}
          comments={metrics?.commentsCount}
          reposts={metrics?.repostsCount}
          className="w-full"
        />
      </div>

      {children}
    </div>
  );
};

export const SectionCard = ({ title, children, icon, description }) => {
  return (
    <div className="border rounded-lg p-4 border-[#262626] flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {icon}
        <h1 className="text-[#FFF] font-[Inter] text-sm not-italic font-semibold leading-[20px]">
          {title}
        </h1>
        <p className="text-[#919191] font-[Inter] text-xs not-italic font-normal leading-[20px]">
          {description}
        </p>
      </div>
      {children}
    </div>
  );
};

export const AnalyzedPostSectionSkeleton = () => {
  return (
    <div className="rounded-lg bg-transparent w-full p-4 space-y-4 border border-[#262626]">
      <div className="space-y-2 animate-pulse">
        <PiSparkleBold size={24} color="#262626" />
        <div className="rounded-full w-1/6 bg-[#262626] h-4"></div>
        <div className="rounded-full w-1/2 bg-[#262626] h-3"></div>
      </div>
      <div className="flex flex-wrap gap-4 animate-pulse">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="rounded-lg w-[250px] h-36 bg-[#262626]"
          ></div>
        ))}
      </div>
    </div>
  );
};

const cardColor = {
  whatsapp: "border-[#60D669] bg-[rgba(96,_214,_105,_0.08)]",
  linktree: "border-[#60D669] bg-[rgba(96,_214,_105,_0.08)]",
  telegram: "border-[#2BACEF] bg-[rgba(43,_172,_239,_0.08)]",
  facebook: "border-[#2BACEF] bg-[rgba(43,_172,_239,_0.08)]",
  instagram: "border-[#E5476D] bg-[rgba(229,_71,_109,_0.08)]",
  linkedin: "border-[#0076B2] bg-[rgba(0,_118,_178,_0.08)]",
  youtube: "border-[#F00] bg-[rgba(255,_0,_0,_0.08)]",
};

export const getPlatformColor = (platform) => {
  let colorClass = "";
  Object.keys(cardColor).forEach((key) => {
    if (platform?.toLowerCase().includes(key)) {
      colorClass = cardColor[key];
    }
  });
  return colorClass;
};

export const AddDataCard = ({ action, title, description }) => {
  return (
    <>
      <div className="min-w-[296px] min-h-[317px] flex flex-col justify-center items-center gap-3 flex-[1_0_0] self-stretch p-5 border border-neutral-800 rounded-md border-dashed">
        <div
          onClick={() => action()}
          className="flex cursor-pointer justify-center items-center gap-2 p-3 border border-[color:var(--slate-200,#E2E8F0)] rounded-[96px] border-solid bg-[#fff] "
        >
          <FaPlus />
        </div>
        <span className="text-white text-center text-xl not-italic font-bold leading-6">
          {title}
        </span>
        <span className="text-neutral-400 text-center text-xs not-italic font-normal leading-5">
          {description}
        </span>
      </div>
    </>
  );
};
export const ChipsCard = ({ value }) => {
  return (
    <div className="flex  border border-neutral-800 rounded-md border-solid bg-[#393738] justify-center items-center gap-2 px-2 py-1.5 text-neutral-400 text-xs not-italic font-normal leading-[normal]">
      {value}
    </div>
  );
};

export const profileStrengthText = {
  1: "POOR",
  2: "AVERAGE",
  3: "GOOD",
  4: "STRONG",
};
export const profileStrengthBackground = {
  1: "bg-[#DC2627] ",
  2: "bg-[#DD7813]",
  3: "bg-[#3771C8]",
  4: "bg-[#2DB737]",
};
